import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import NameWithEmoji from 'src/components/name_with_emoji/name_with_emoji.vue'
import ReplyArrow from 'src/components/reply_arrow/reply_arrow.vue'
import UnicodeDomainIndicator from 'src/components/unicode_domain_indicator/unicode_domain_indicator.vue'
import UserAvatar from 'src/components/user_avatar/user_avatar.vue'
import UserPopover from 'src/components/user_popover/user_popover.vue'
import {
  highlightClass,
  highlightStyle,
} from '../../services/user_highlighter/user_highlighter.js'

import { useInstanceStore } from 'src/stores/instance.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useUserHighlightStore } from 'src/stores/user_highlight.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  profileLink,
  userInitial,
} from 'src/services/user_display/user_display.js'

// One document listener for all mention links. The full handle is shown
// while a link is in the selection, so a copy gets @user@host. Typing moves
// a collapsed selection: then only the marked links need clearing.
const mountedLinks = new Set()
const selectedLinks = new Set()

const onSelectionChange = () => {
  const selection = document.getSelection()
  if (selection.isCollapsed) {
    selectedLinks.forEach((link) => {
      link.hasSelection = false
    })
    selectedLinks.clear()
    return
  }
  mountedLinks.forEach((link) => {
    if (!link.$refs.full) return
    link.hasSelection = selection.containsNode(link.$refs.full, true)
    if (link.hasSelection) selectedLinks.add(link)
    else selectedLinks.delete(link)
  })
}

const MentionLink = {
  name: 'MentionLink',
  components: {
    UserAvatar,
    NameWithEmoji,
    UnicodeDomainIndicator,
    UserPopover,
    ReplyArrow,
    // shows a post, which has rich content: no import cycle
    StatusPopover: defineAsyncComponent(
      () => import('src/components/status_popover/status_popover.vue'),
    ),
  },
  inject: {
    gotoReplyParent: { default: null },
  },
  props: {
    url: {
      required: false,
      type: String,
    },
    content: {
      required: true,
      type: String,
    },
    userId: {
      required: false,
      type: String,
    },
    userScreenName: {
      required: false,
      type: String,
    },
    // the pill as just the avatar (the mentions a post starts with); the
    // name stays in the label and the hover card
    avatarOnly: Boolean,
    // { statusId, plain } when this is the person the post replies to: a
    // pill then carries the reply sign, previews the replied-to post on
    // hover and goes there on a click (plain: only the sign, in previews)
    replyTo: { type: Object, default: null },
  },
  data() {
    return {
      hasSelection: false,
      avatarFailed: false,
    }
  },
  methods: {
    onClick() {
      if (this.replyPreview) {
        this.gotoReplyParent?.()
        return
      }
      if (this.shouldShowTooltip) return
      const link = profileLink({
        id: this.userId || this.user.id,
        screen_name: this.userScreenName || this.user.screen_name,
      })
      this.$router.push(link)
    },
  },
  mounted() {
    if (mountedLinks.size === 0) {
      document.addEventListener('selectionchange', onSelectionChange)
    }
    mountedLinks.add(this)
  },
  unmounted() {
    mountedLinks.delete(this)
    selectedLinks.delete(this)
    if (mountedLinks.size === 0) {
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  },
  computed: {
    // the reply sign inside the pill; text mentions keep the arrow in front
    replyInPill() {
      return !!this.replyTo && !!this.user && this.isPill
    },
    // hover shows the replied-to post instead of the person
    replyPreview() {
      return this.replyInPill && !!this.replyTo.statusId && !this.replyTo.plain
    },
    popoverProps() {
      return this.replyPreview
        ? { statusId: this.replyTo.statusId }
        : { userId: this.user.id, disabled: !this.shouldShowTooltip }
    },
    pillLabel() {
      if (this.replyInPill) {
        return `${this.$t('status.reply_to')} ${this.pillName}`
      }
      return this.avatarOnly ? this.pillName : null
    },
    user() {
      return this.url ? useUsersStore().findUserByUrl(this.url) : null
    },
    isYou() {
      if (!this.currentUser) return false
      return this.user === this.currentUser
    },
    userName() {
      return this.user && this.userNameFullUi.split('@')[0]
    },
    serverName() {
      // XXX assumed that domain does not contain @
      return (
        this.user &&
        (this.userNameFullUi.split('@')[1] || useInstanceStore().instanceDomain)
      )
    },
    userNameFull() {
      return this.user?.screen_name
    },
    userNameFullUi() {
      return this.user?.screen_name_ui
    },
    highlightData() {
      return this.highlight[this.user?.screen_name]
    },
    highlightType() {
      return this.highlightData && '-' + this.highlightData.type
    },
    highlightClass() {
      return this.highlightData && highlightClass(this.user)
    },
    style() {
      if (this.highlightData) {
        const {
          backgroundColor,
          backgroundPosition,
          backgroundImage,
          ...rest
        } = highlightStyle(this.highlightData)
        return rest
      }
    },
    classnames() {
      return [
        {
          '-you': this.isYou && this.shouldBoldenYou,
          '-highlighted': !!this.highlightData,
          '-has-selection': this.hasSelection,
        },
        this.highlightType,
      ]
    },
    isRemote() {
      return this.userName !== this.userNameFull
    },
    isPill() {
      return this.mergedConfig.mentionLinkDisplay === 'pill'
    },
    initial() {
      return userInitial(this.user)
    },
    pillName() {
      return this.user.name_unescaped || this.userName
    },
    shouldShowFullUserName() {
      const conf = this.mergedConfig.mentionLinkDisplay
      if (conf === 'short') {
        return false
      } else if (conf === 'full') {
        return true
      } else {
        // full_for_remote
        return this.isRemote
      }
    },
    shouldShowTooltip() {
      return this.mergedConfig.mentionLinkShowTooltip
    },
    shouldShowAvatar() {
      return this.mergedConfig.mentionLinkShowAvatar
    },
    shouldShowYous() {
      return this.mergedConfig.mentionLinkShowYous
    },
    shouldBoldenYou() {
      return this.mergedConfig.mentionLinkBoldenYou
    },
    shouldFadeDomain() {
      return this.mergedConfig.mentionLinkFadeDomain
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
    ...mapState(useUserHighlightStore, ['highlight']),
    ...mapState(useUsersStore, ['currentUser']),
  },
}

export default MentionLink
